import { useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import { useBlobUrl } from '~/hooks/useBlobUrl'
import type { PhotoRecord } from '~/models/photo'
import { formatCalendarDate } from '~/utils/format'
import styles from './PhotoComparison.module.css'

/**
 * Comparison modes (Checkpoint 5.2).
 *
 * `ui-ux-spec.md` asks for side-by-side, swipe, flicker and silhouette overlay,
 * "with explicit controls as an accessible alternative" to the gestures.
 *
 * **Every mode here is driven by a native control rather than by a bespoke
 * gesture handler**, which is not a shortcut — a `range` input is draggable by
 * thumb, operable by arrow key, announced by VoiceOver and focusable by a
 * switch, so one control is both the gesture and its alternative. A custom
 * touch handler would have needed a second, separate control built beside it,
 * and the two would drift.
 *
 * **Project Sola never says what changed.** It puts two photos together and
 * stops; reading them is the user's. Anything else would be the appearance
 * judgement `ui-ux-spec.md` forbids.
 */

export type ComparisonMode = 'sideBySide' | 'reveal' | 'flicker' | 'overlay'

const MODES: readonly { value: ComparisonMode; label: string; hint: string }[] = [
  { value: 'sideBySide', label: 'Side by side', hint: 'Both photos, next to each other.' },
  { value: 'reveal', label: 'Reveal', hint: 'Drag to wipe between them.' },
  { value: 'flicker', label: 'Flicker', hint: 'Switch between one and the other.' },
  { value: 'overlay', label: 'Overlay', hint: 'Fade one through the other.' },
]

interface PhotoComparisonProps {
  readonly earliest: PhotoRecord
  readonly latest: PhotoRecord
  readonly label: string
  /** What the pair represents, for the caption. */
  readonly pairDescription: string
}

export function PhotoComparison({
  earliest,
  latest,
  label,
  pairDescription,
}: PhotoComparisonProps) {
  const [mode, setMode] = useState<ComparisonMode>('sideBySide')
  const [position, setPosition] = useState(50)
  const [showLatest, setShowLatest] = useState(true)

  const earliestUrl = useBlobUrl(earliest.blobId)
  const latestUrl = useBlobUrl(latest.blobId)

  const earliestAlt = `${label} view, ${formatCalendarDate(earliest.capturedDate)}`
  const latestAlt = `${label} view, ${formatCalendarDate(latest.capturedDate)}`

  return (
    <figure class={styles.comparison}>
      <fieldset class={styles.modes}>
        <legend class={styles.legend}>How to compare</legend>
        {MODES.map((option) => (
          <label key={option.value} class={styles.mode}>
            <input
              type="radio"
              name={`comparison-mode-${label}`}
              value={option.value}
              checked={mode === option.value}
              onChange={() => setMode(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>

      {/* The chosen mode's hint, rather than four of them at once: on a 390 px
          screen four hints under four radios is more words than photographs. */}
      <p class={styles.readout}>{MODES.find((option) => option.value === mode)?.hint}</p>

      {mode === 'sideBySide' ? (
        <div class={styles.pair}>
          <div class={styles.side}>
            <img src={earliestUrl} alt={earliestAlt} class={styles.image} />
            <span class={styles.date}>{formatCalendarDate(earliest.capturedDate)}</span>
          </div>
          <div class={styles.side}>
            <img src={latestUrl} alt={latestAlt} class={styles.image} />
            <span class={styles.date}>{formatCalendarDate(latest.capturedDate)}</span>
          </div>
        </div>
      ) : null}

      {mode === 'reveal' ? (
        <div class={styles.stack}>
          <img src={earliestUrl} alt={earliestAlt} class={styles.stackImage} />
          {/* The later photo is clipped to the slider position. `clip-path`
              rather than width, so the image is never squashed — reshaping a
              body is exactly what ui-ux-spec.md forbids. */}
          <img
            src={latestUrl}
            alt={latestAlt}
            class={styles.stackImage}
            style={{ clipPath: `inset(0 0 0 ${position}%)` }}
          />
          <div class={styles.divider} style={{ left: `${position}%` }} aria-hidden="true" />
        </div>
      ) : null}

      {mode === 'overlay' ? (
        <div class={styles.stack}>
          <img src={earliestUrl} alt={earliestAlt} class={styles.stackImage} />
          <img
            src={latestUrl}
            alt={latestAlt}
            class={styles.stackImage}
            style={{ opacity: position / 100 }}
          />
        </div>
      ) : null}

      {mode === 'flicker' ? (
        <div class={styles.stack}>
          <img
            src={showLatest ? latestUrl : earliestUrl}
            alt={showLatest ? latestAlt : earliestAlt}
            class={styles.stackImage}
          />
        </div>
      ) : null}

      {mode === 'reveal' || mode === 'overlay' ? (
        <div class={styles.control}>
          <label class={styles.controlLabel} for={`position-${label}`}>
            {mode === 'reveal' ? 'Wipe position' : 'Blend'}
          </label>
          <input
            id={`position-${label}`}
            class={styles.slider}
            type="range"
            min={0}
            max={100}
            value={position}
            onInput={(event) =>
              setPosition(Number((event.currentTarget as HTMLInputElement).value))
            }
            aria-valuetext={`${position}% towards ${formatCalendarDate(latest.capturedDate)}`}
          />
          <p class={styles.readout}>
            {position}% towards {formatCalendarDate(latest.capturedDate)}
          </p>
        </div>
      ) : null}

      {mode === 'flicker' ? (
        <div class={styles.control}>
          {/* A button rather than a timer: an automatic flicker is motion the
              user did not ask for, and would have to be suppressed under
              prefers-reduced-motion anyway. Tapping is also more useful — the
              user controls when the switch happens. */}
          <Button variant="secondary" onClick={() => setShowLatest((current) => !current)}>
            Show{' '}
            {showLatest
              ? formatCalendarDate(earliest.capturedDate)
              : formatCalendarDate(latest.capturedDate)}
          </Button>
          <p class={styles.readout} role="status">
            Showing {showLatest ? latestAlt : earliestAlt}
          </p>
        </div>
      ) : null}

      <figcaption class={styles.caption}>
        {pairDescription} Project Sola shows them together and says nothing about what has changed —
        that is yours to read.
      </figcaption>
    </figure>
  )
}
